import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import LoadingButton from "@mui/lab/LoadingButton";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import type { SelectChangeEvent } from '@mui/material/Select';
import Button from '@mui/material/Button';
import { Checkbox, Divider, FormControlLabel, MenuItem, Select, TextField } from '@mui/material';
import '../../components/Invoice/Invoice.css';
import { isEmpty } from 'lodash';

import { IUserFormInputs } from '../../utils/user.types';
import { IOptions } from '../../components/FormFields/CustomSelect';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { ApiStatus } from '../../utils/api.types';
import { addUser, fetchUserById, updateUser } from '../../store/userThunk';
import RoleService from '../../services/RoleService';

// Schema to validate user form.
const schema = yup.object().shape({
    name: yup.string().required('Name is required.'),
    email: yup.string().required('Owner email is required.'),
    isActive: yup.boolean(),
    roles: yup.array().of(yup.string()).nullable(),
});

interface ILocationState {
    accountId: string;
}

/**
 * Component to create/edit user.
 */
const UserForm = () => {
    const { userId = '' } = useParams();
    const { state } = useLocation();

    // use form hook to mange user form state.
    const methods = useForm<IUserFormInputs>({
        resolver: yupResolver(schema),
    });

    // States
    const [roleOptions, setRoleOptions] = useState<IOptions[]>([]);
    const [userRoles, setUserRoles] = useState<string[] | undefined>([]);
    const [isActive, setIsActive] = useState(true);

    const { status, users, userById } = useAppSelector((state => state.users));
    const { selectedAccount, user } = useAppSelector((state => state.auth));

    const [userAccountId, setUserAccountId] = useState([selectedAccount._id]);
    const [editUserData, setEditUserData] = useState({} as IUserFormInputs);
    const isEditing = !!userId;

    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    /**
     * Method to go back to previous route.
     */
    const navigateBack = () => {
        if (state) {
            const accountId = (state as ILocationState).accountId;

            navigate(`/account/account-details/${accountId}/account-users`);

        } else {
            navigate('/users');
        }
    }

    /**
     * Method to handle user form submit.
     */
    const onSubmit = async (data: IUserFormInputs) => {
        // Dispatch add user action.
        try {
            data.accounts = userAccountId;
            if (!isEditing) {
                await dispatch(addUser(data)).unwrap();
            } else {
                await dispatch(updateUser({ userUpdatePayload: data, id: userId })).unwrap();
            }

            navigateBack();
        } catch (error: any) {
            // Empty catch block.
        }
    };

    /**
     * If state is there is means user is being created in specific account.
     * Set specific account id.
     */
    useEffect(() => {
        if (state) {
            const accountId = (state as ILocationState).accountId;
            setUserAccountId([accountId]);
        }
    }, [state]);

    // Opening the edit URL directly leaves the users list empty, so load the member itself
    useEffect(() => {
        if (userId && !users.some(u => u._id === userId)) {
            dispatch(fetchUserById(userId));
        }
    }, [dispatch, userId, users]);

    /**
     * Set edit user data in case of edit.
     */
    useEffect(() => {
        if (!userId) return;
        const editUser: any = users.find(u => u._id === userId) || (userById?._id === userId ? userById : null);

        if (editUser) {
            const { name, email, isActive } = editUser;
            // List API returns `roles`, single-user API returns `role`
            const roleId = editUser.roles?.[0]?._id || editUser.role?._id;
            setEditUserData({ name, email, isActive, roles: roleId ? [roleId] : [], accounts: userAccountId });
        }
    }, [userId, users, userById, userAccountId]);

    /**
     * Fetch Roles on User form load.
     */
    useEffect(() => {
        const fetchRoles = async () => {
            const rolesOptions = await RoleService.fetchRoles();
            setRoleOptions(rolesOptions || []);
        }
        fetchRoles();
    }, []);

    // Set roles in case of edit.
    useEffect(() => {
        if (isEditing && !isEmpty(editUserData)) {
            setUserRoles(editUserData.roles);
            setIsActive(editUserData.isActive);

            // Set form data with default values in case of edit.
            methods.reset(editUserData);
        }
    }, [isEditing, editUserData, methods]);

    const isSelf = userId === user?._id;
    const fieldSx = { width: '100%', mt: 0.5, borderRadius: '8px!important' };
    const errorSx = { color: "#c33b3b", fontSize: '12px', margin: '4px 0 0 0' };

    return (
        <Box sx={{ mt: 1 }}>
            <FormProvider {...methods}>
                <Box
                    component="form"
                    onSubmit={methods.handleSubmit(onSubmit)}
                    noValidate
                >
                    <div className={'c-card2'}>
                        <div style={{ padding: 15, paddingTop: 10, paddingBottom: 15 }}>
                            <h5 style={{ fontWeight: 'bold', color: '#000', fontSize: 15 }}>
                                {isEditing ? 'Edit Member' : 'Create A Member'}
                            </h5>
                            <Divider sx={{ mt: 0.5, mb: 2 }} />

                            <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                    <h5 className='c-label2'>Name*</h5>
                                    <TextField
                                        placeholder='Name'
                                        {...methods.register('name')}
                                        className='c-text-field-4'
                                        variant="standard"
                                        size='small'
                                        sx={fieldSx}
                                        InputProps={{ disableUnderline: true }}
                                    />
                                    <p style={errorSx}>{methods.formState.errors.name?.message ?? ""}</p>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <h5 className='c-label2'>Email*</h5>
                                    <TextField
                                        placeholder='Email'
                                        {...methods.register('email')}
                                        className='c-text-field-4'
                                        variant="standard"
                                        size='small'
                                        sx={fieldSx}
                                        InputProps={{ disableUnderline: true }}
                                    />
                                    <p style={errorSx}>{methods.formState.errors.email?.message ?? ""}</p>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <h5 className='c-label2'>User Role</h5>
                                    <Select
                                        value={userRoles?.[0] ?? ''}
                                        onChange={(event: SelectChangeEvent<any>) => {
                                            setUserRoles([event.target.value]);
                                            // Backend reads roles[0], so this must stay an array
                                            methods.setValue("roles", [event.target.value], { shouldValidate: true });
                                        }}
                                        displayEmpty
                                        disabled={isSelf}
                                        size='small'
                                        renderValue={(value: any) => roleOptions.find(o => o.value === value)?.label
                                            || <span style={{ color: '#9da7b7' }}>Select Role</span>}
                                        sx={{ width: '100%', mt: 0.5, height: 35, bgcolor: '#fff', borderRadius: '10px', '& fieldset': { borderColor: '#9da7b7' } }}
                                    >
                                        {roleOptions.map(option => (
                                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                        ))}
                                    </Select>
                                    {isSelf && <p style={{ color: '#9da7b7', fontSize: '12px', margin: '4px 0 0 0' }}>You can’t change your own role.</p>}
                                </Grid>

                                {isEditing && (
                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={isActive ?? false}
                                                    onChange={(event, checked) => {
                                                        setIsActive(checked);
                                                        methods.setValue('isActive', checked);
                                                    }}
                                                    size='small'
                                                />
                                            }
                                            label={<span style={{ fontSize: 14 }}>Is Active</span>}
                                        />
                                    </Grid>
                                )}
                            </Grid>

                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', columnGap: 2 }}>
                                <Button variant='outlined' onClick={navigateBack}>
                                    Cancel
                                </Button>
                                <LoadingButton
                                    type="submit"
                                    loading={status === ApiStatus.LOADING}
                                    variant="contained"
                                    color='secondary'
                                >
                                    {isEditing ? 'Update' : 'Create'}
                                </LoadingButton>
                            </Box>
                        </div>
                    </div>
                </Box>
            </FormProvider>
        </Box>
    );
}

export default UserForm;
